import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Search, 
  SlidersHorizontal, 
  ChevronRight, 
  Sparkles,
  Package,
  Loader2,
  ArrowRight,
  Grid3X3
} from 'lucide-react';
import { motion } from 'framer-motion';
import { optimizeImageUrl } from '../utils/imageUrl';

// Fallback images for categories
const CATEGORY_IMAGES = {
  'abbigliamento-e-accessori': 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=600&q=80',
  'gioielli': 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=600&q=80',
  'arte': 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=600&q=80',
  'oggetti': 'https://images.unsplash.com/photo-1606760227091-3dd870d97f1d?w=600&q=80',
  'casa': 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&q=80',
  'eventi': 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&q=80',
  'intrattenimento': 'https://images.unsplash.com/photo-1558060370-d644479cb6f7?w=600&q=80',
  'bellezza': 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=600&q=80',
  'animali-domestici': 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=600&q=80',
  'donna': 'https://images.unsplash.com/photo-1557161622-5f50ca344787?w=600&q=80',
  'uomo': 'https://images.unsplash.com/photo-1617137968427-85924c800a22?w=600&q=80',
  'bambino': 'https://images.pexels.com/photos/15704149/pexels-photo-15704149.jpeg?w=600&q=80',
  'accessori': 'https://images.unsplash.com/photo-1606760227091-3dd870d97f1d?w=600&q=80',
  'orologi': 'https://images.unsplash.com/photo-1524592094714-0f0654e20314?w=600&q=80',
  'bracciali': 'https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=600&q=80',
  'anelli': 'https://images.pexels.com/photos/2732096/pexels-photo-2732096.jpeg?w=600&q=80',
  'orecchini': 'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=600&q=80',
  'collane': 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=600&q=80',
  'pittura': 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=600&q=80',
  'scultura': 'https://images.unsplash.com/photo-1544531586-fde5298cdd40?w=600&q=80',
  'ceramiche': 'https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?w=600&q=80',
  'portachiavi': 'https://images.unsplash.com/photo-1622838320226-2d88bffa4a7c?w=600&q=80',
  'arredamento': 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&q=80',
  'illuminazione': 'https://images.unsplash.com/photo-1507473885765-e6ed057f782c?w=600&q=80',
  'tessuti': 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=600&q=80',
  'feste': 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&q=80',
  'matrimoni': 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600&q=80',
  'cosplay': 'https://images.unsplash.com/photo-1608889825103-eb5ed706fc64?w=600&q=80',
  'giochi': 'https://images.unsplash.com/photo-1558060370-d644479cb6f7?w=600&q=80',
  'musica': 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=600&q=80',
  'skincare': 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=600&q=80',
  'profumi': 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=600&q=80',
};

const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=600&q=80';

// Get image for a category
const getCategoryImage = (category) => {
  const slug = category.slug?.toLowerCase() || category.name?.toLowerCase().replace(/\s+/g, '-');
  const base = category.image_url || CATEGORY_IMAGES[slug] || DEFAULT_IMAGE;
  return optimizeImageUrl(base, 600);
};

export default function ExplorePage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [productCounts, setProductCounts] = useState({});
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Load categories from database
  useEffect(() => {
    let cancelled = false;

    const fetchWithTimeout = async (url, ms = 10000) => {
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), ms);
      try {
        return await fetch(url, { signal: controller.signal });
      } finally {
        clearTimeout(t);
      }
    };

    const fetchCategories = async () => {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      // Retry per gestire il cold start del backend (Railway)
      let lastErr;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const response = await fetchWithTimeout(`${API_URL}/api/categories/public`, 10000);
          const data = await response.json();
          if (!data.categories) throw new Error('No categories data');
          return data.categories;
        } catch (err) {
          lastErr = err;
          await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
        }
      }
      throw lastErr;
    };

    const loadCategories = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const allCategories = await fetchCategories();
        if (cancelled) return;

        // Build hierarchy: parent categories with children
        const parentCategories = allCategories.filter(c => !c.parent_id);
        const childrenMap = {};

        allCategories.forEach(cat => {
          if (cat.parent_id) {
            if (!childrenMap[cat.parent_id]) {
              childrenMap[cat.parent_id] = [];
            }
            childrenMap[cat.parent_id].push(cat);
          }
        });

        // Sort children by sort_order
        Object.keys(childrenMap).forEach(parentId => {
          childrenMap[parentId].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        });

        // Attach children to parents and sort parents
        const categoriesWithChildren = parentCategories
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map(parent => ({
            ...parent,
            children: childrenMap[parent.id] || []
          }));

        setCategories(categoriesWithChildren);
        setLoading(false);

        // Load product counts per category (non bloccante: errori ignorati)
        try {
          const { data: products } = await supabase
            .from('products')
            .select('category')
            .eq('status', 'active');
          if (products && !cancelled) {
            const counts = {};
            products.forEach(p => {
              if (p.category) {
                counts[p.category] = (counts[p.category] || 0) + 1;
              }
            });
            setProductCounts(counts);
          }
        } catch (countErr) {
          // I conteggi sono opzionali: non devono impedire il render del catalogo
        }
      } catch (error) {
        console.error('Error loading categories:', error);
        if (!cancelled) {
          setLoadError(true);
          setLoading(false);
        }
      }
    };

    loadCategories();
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Filter categories by search
  const filteredCategories = categories.filter(cat => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const matchesParent = cat.name?.toLowerCase().includes(query);
    const matchesChild = cat.children?.some(child => 
      child.name?.toLowerCase().includes(query)
    );
    return matchesParent || matchesChild;
  });

  // Count total products in a category (including children)
  const getTotalProducts = (category) => {
    let total = productCounts[category.name] || 0;
    category.children?.forEach(child => {
      total += productCounts[child.name] || 0;
    });
    return total;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#008080]" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="text-center max-w-md" data-testid="explore-load-error">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">
            Impossibile caricare il catalogo
          </h3>
          <p className="text-slate-500 mb-6">
            Si è verificato un problema di connessione. Riprova tra un istante.
          </p>
          <Button
            onClick={() => setReloadKey((k) => k + 1)}
            className="bg-[#008080] hover:bg-[#006666]"
            data-testid="explore-retry-button"
          >
            Riprova
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Esplora il Catalogo | Artigianato Made in Italy | Rarifex</title>
        <meta name="description" content="Esplora il catalogo di prodotti artigianali Made in Italy. Scopri gioielli, arte, abbigliamento, arredamento e molto altro, tutto fatto a mano da artigiani italiani." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#008080] via-[#006666] to-[#004d4d] py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.2)_0%,transparent_50%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(255,255,255,0.15)_0%,transparent_40%)]" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Badge className="bg-white/20 text-white border-0 mb-6 px-4 py-2">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Oltre 250 categorie artigianali
                </Badge>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Esplora il Catalogo
                </h1>
                
                <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-2xl mx-auto">
                  Lasciati ispirare dalle creazioni dei nostri artigiani italiani.
                  Naviga per categoria e scopri pezzi unici fatti a mano.
                </p>

                {/* Quick Search */}
                <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Cerca una categoria..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-12 py-6 text-lg bg-white/95 border-0 shadow-lg"
                      data-testid="explore-search-input"
                    />
                  </div>
                  <Link to="/products">
                    <Button 
                      variant="outline" 
                      className="py-6 px-6 bg-white/10 border-white/30 text-white hover:bg-white hover:text-[#008080] whitespace-nowrap"
                      data-testid="advanced-search-button"
                    >
                      <SlidersHorizontal className="w-5 h-5 mr-2" />
                      Ricerca Avanzata
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Categories Grid */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            
            {/* Section Header */}
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-800">
                  Tutte le Categorie
                </h2>
                <p className="text-slate-500 mt-1">
                  {filteredCategories.length} categorie principali
                </p>
              </div>
              <Link to="/products">
                <Button variant="ghost" className="text-[#008080] hover:text-[#006666]">
                  <Grid3X3 className="w-5 h-5 mr-2" />
                  Vedi tutti i prodotti
                </Button>
              </Link>
            </div>

            {/* Categories */}
            {filteredCategories.length === 0 ? (
              <div className="text-center py-16">
                <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-600 mb-2">
                  Nessuna categoria trovata
                </h3>
                <p className="text-slate-500">
                  Prova con un altro termine di ricerca
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                {filteredCategories.map((category, index) => (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                  >
                    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 border-0 shadow-md">
                      <div className="flex flex-col lg:flex-row">
                        
                        {/* Category Image */}
                        <Link 
                          to={`/categoria/${category.slug || category.name?.toLowerCase().replace(/\s+/g, '-')}`}
                          className="lg:w-80 h-48 lg:h-auto flex-shrink-0 relative group"
                        >
                          <img
                            src={getCategoryImage(category)}
                            alt={category.name}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                          <div className="absolute bottom-4 left-4 right-4">
                            <h3 className="text-2xl font-bold text-white mb-1">
                              {category.name}
                            </h3>
                            {getTotalProducts(category) > 0 && (
                              <Badge className="bg-white/20 text-white border-0">
                                {getTotalProducts(category)} prodotti
                              </Badge>
                            )}
                          </div>
                        </Link>

                        {/* Subcategories */}
                        <div className="flex-1 p-6">
                          {category.children && category.children.length > 0 ? (
                            <>
                              <div className="flex items-center justify-between mb-4">
                                <p className="text-sm text-slate-500 font-medium uppercase tracking-wider">
                                  Sottocategorie
                                </p>
                                <Link 
                                  to={`/categoria/${category.slug || category.name?.toLowerCase().replace(/\s+/g, '-')}`}
                                  className="text-sm text-[#008080] hover:text-[#006666] font-medium flex items-center gap-1"
                                >
                                  Vedi tutto
                                  <ChevronRight className="w-4 h-4" />
                                </Link>
                              </div>
                              
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                {category.children
                                  .slice(0, expandedCategory === category.id ? category.children.length : 10)
                                  .map((child) => (
                                    <Link
                                      key={child.id}
                                      to={`/categoria/${category.slug || category.name?.toLowerCase().replace(/\s+/g, '-')}/${child.slug || child.name?.toLowerCase().replace(/\s+/g, '-')}`}
                                      className="group"
                                    >
                                      <div className="relative aspect-square rounded-xl overflow-hidden bg-slate-100">
                                        <img
                                          src={getCategoryImage(child)}
                                          alt={child.name}
                                          loading="lazy"
                                          decoding="async"
                                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent group-hover:from-black/80" />
                                        <div className="absolute bottom-0 left-0 right-0 p-3">
                                          <p className="text-white text-sm font-medium leading-tight">
                                            {child.name}
                                          </p>
                                          {productCounts[child.name] > 0 && (
                                            <p className="text-white/70 text-xs mt-0.5">
                                              {productCounts[child.name]} prodotti
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </Link>
                                  ))}
                              </div>

                              {/* Show More Button */}
                              {category.children.length > 10 && (
                                <button
                                  onClick={() => setExpandedCategory(
                                    expandedCategory === category.id ? null : category.id
                                  )}
                                  className="mt-4 text-sm text-[#008080] hover:text-[#006666] font-medium flex items-center gap-1"
                                >
                                  {expandedCategory === category.id ? (
                                    <>Mostra meno</>
                                  ) : (
                                    <>
                                      Mostra altre {category.children.length - 10} sottocategorie
                                      <ArrowRight className="w-4 h-4" />
                                    </>
                                  )}
                                </button>
                              )}
                            </>
                          ) : (
                            <div className="h-full flex items-center justify-center">
                              <Link 
                                to={`/categoria/${category.slug || category.name?.toLowerCase().replace(/\s+/g, '-')}`}
                                className="text-center"
                              >
                                <Button className="bg-[#008080] hover:bg-[#006666]">
                                  Esplora {category.name}
                                  <ChevronRight className="w-5 h-5 ml-2" />
                                </Button>
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-slate-100 to-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                Non trovi quello che cerchi?
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Usa la ricerca avanzata per filtrare per prezzo, materiale, o parole chiave specifiche.
                Oppure richiedi un prodotto su misura direttamente agli artigiani.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/products">
                  <Button 
                    size="lg" 
                    className="bg-[#008080] hover:bg-[#006666] py-6 px-8"
                    data-testid="cta-advanced-search"
                  >
                    <SlidersHorizontal className="w-5 h-5 mr-2" />
                    Ricerca Avanzata
                  </Button>
                </Link>
                <Link to="/prodotto-su-misura">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="py-6 px-8 border-[#008080] text-[#008080] hover:bg-[#008080] hover:text-white"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Richiedi Su Misura
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}
